// frontend/src/pages/KPIs.js  (v4 — Tech Team Recognition & Incentive Program)
// Role-specific KPIs (Developer/QA/PM), quarterly champion, updated scoring tiers
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useActiveDepartment } from '../context/DepartmentContext';
import {
  getKPITemplates, createKPITemplate, updateKPITemplate, deleteKPITemplate,
  getKPIAssignments, assignKPI, bulkAssignKPIs, archiveKPISet, reassignKPISet, deleteKPIAssignment,
  submitSelfEvaluation, submitManagerScores,
  getBonusPreview, generateBonus, getTeamBonus,
  getQuarterlyScores, declareChampion, getChampions,
  getUsers, getCycles, addDevPlanItem, getRoleTypes, getDepartments,
  getCarryForwardSource, carryForwardKPIs, diagnoseSetup
} from '../services/api';
import { kpiScoreSummary } from '../utils/kpiScore';
import { toCSV, downloadCSV } from '../utils/csv';
import {
  Card, Avatar, Badge, Btn, Modal, Field, Input, Select, Textarea,
  SectionTitle, Spinner, ProgressBar,
  CALIBRATION_MAP, KPI_SCORE_LABELS,
  scoreToCalibration, scoreToLabel
} from '../components/UI';
import PageHeader from '../components/PageHeader';

const CAT_COLOR = {
  Delivery:'green', 'Code Quality':'blue', Productivity:'amber',
  'Technical Excellence':'blue', Collaboration:'gray', Process:'gray',
  Reliability:'red', Learning:'green',
  'Test Effectiveness':'blue', Quality:'red', Execution:'amber',
  Coverage:'green', Automation:'blue', Timeliness:'amber',
  Impact:'green',
  'Strategic Planning & Delivery':'amber',
  'Technical Architecture & Ownership':'blue',
  'Full-Stack Development':'green',
  'Team Leadership & Mentorship':'gray',
};



function scoreColor(s) {
  if (!s) return 'var(--color-text-tertiary)';
  const cal = scoreToCalibration(s);
  return CALIBRATION_MAP[cal]?.color || 'var(--color-text-tertiary)';
}

function RatingBadge({ score }) {
  if (score == null) return null;
  const cal = scoreToCalibration(score);
  const r = CALIBRATION_MAP[cal];
  if (!r) return null;
  return (
    <span style={{ display:'inline-flex', padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:500, background:r.bg, color:r.color }}>
      {r.label}
    </span>
  );
}

function Stars({ value, onChange, readOnly }) {
  return (
    <div style={{ display:'flex', gap:3 }}>
      {[1,2,3,4,5].map(n => (
        <button key={n} type="button" onClick={() => !readOnly && onChange?.(n)}
          style={{
            width:24, height:24, border:'none', borderRadius:5,
            cursor:readOnly?'default':'pointer', fontSize:11, fontWeight:500, fontFamily:'inherit',
            background:n<=(value||0)?'#1F3B68':'var(--color-background-secondary)',
            color:n<=(value||0)?'#fff':'var(--color-text-tertiary)',
          }}>{n}</button>
      ))}
    </div>
  );
}

function WeightBar({ kpis }) {
  const colors = ['#2C4F86','#378ADD','#EF9F27','#E24B4A','#7F77DD','#FFCB05','#D85A30','#D4537E','#888780','#2C4F86'];
  const total = kpis.reduce((s,k)=>s+Number(k.weight),0);
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'var(--color-text-tertiary)', marginBottom:4 }}>
        <span>Weight distribution (must sum to 100%)</span>
        <span style={{ color:Math.abs(total-100)<0.5?'var(--navy-600)':'var(--red-700)', fontWeight:500 }}>{total.toFixed(0)}% / 100%</span>
      </div>
      <div style={{ display:'flex', height:8, borderRadius:6, overflow:'hidden' }}>
        {kpis.map((k,i)=>(
          <div key={k.id} style={{ width:`${k.weight}%`, background:colors[i%colors.length], transition:'width 0.3s' }} title={`${k.title}: ${k.weight}%`} />
        ))}
        {total < 100 && <div style={{ flex:1, background:'var(--color-background-secondary)' }} />}
      </div>
    </div>
  );
}

/** Short "12 Mar" for a timestamp; empty string when there isn't one. */
function shortDate(v) {
  if (!v) return '';
  const d = new Date(String(v).length <= 10 ? `${v}T00:00:00` : v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

/**
 * A rating and the comment written with it, read-only.
 *
 * The manager's per-KPI comment used to live ONLY inside the manager scoring
 * modal (a textarea behind an isManager gate), so the person being rated could
 * see the number their manager gave and never the reason for it — the narrative
 * review's summary_notes was the only manager writing that reached them.
 * Rendered in full, never ellipsis-truncated: this is the feedback.
 */
function ScoreNote({ label, who, score, comment, when }) {
  if (score == null && !comment) return null;
  return (
    <div style={{ borderLeft:'2px solid var(--border-md)', paddingLeft:10, marginTop:8 }}>
      <div style={{ display:'flex', alignItems:'baseline', gap:8, flexWrap:'wrap' }}>
        <span style={{ fontSize:9.5, textTransform:'uppercase', letterSpacing:'0.5px', color:'var(--text-3)', fontWeight:600 }}>{label}</span>
        {score != null && (
          <span style={{ fontSize:11.5, color:'var(--text-2)', fontWeight:600 }}>
            {score} · {KPI_SCORE_LABELS[Math.round(Number(score))]}
          </span>
        )}
        {(who || when) && (
          <span style={{ fontSize:10.5, color:'var(--text-3)' }}>
            {[who, shortDate(when)].filter(Boolean).join(' · ')}
          </span>
        )}
      </div>
      {comment
        ? <div style={{ fontSize:12, color:'var(--text-2)', lineHeight:1.6, marginTop:3, whiteSpace:'pre-wrap' }}>{comment}</div>
        : <div style={{ fontSize:11.5, color:'var(--text-3)', fontStyle:'italic', marginTop:3 }}>No comment left.</div>}
    </div>
  );
}

function DownloadIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

export default function KPIs({ mode = 'evaluate' }) {
  const { user } = useAuth();
  const { activeDepartmentId } = useActiveDepartment();
  const navigate = useNavigate();
  const [openEmp, setOpenEmp] = useState(null);   // evaluate mode: one expanded person at a time
  const [templates,    setTemplates]    = useState([]);
  const [assignments,  setAssignments]  = useState([]);
  const [carryBusy,    setCarryBusy]    = useState(false);   // carry-forward in flight
  // Why is this list empty? The generic "No KPIs assigned" never said which step
  // of the chain was missing — this holds the server's answer.
  const [diagnosis,    setDiagnosis]    = useState(null);
  const [users,        setUsers]        = useState([]);
  const [cycles,       setCycles]       = useState([]);
  const [champions,    setChampions]    = useState([]);
  const [quarterlyScores, setQuarterlyScores] = useState([]);
  const [teamBonus,    setTeamBonus]    = useState([]);
  const [roleTypes,    setRoleTypes]    = useState([]);
  const [departments,  setDepartments]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [activeTab,    setActiveTab]    = useState('kpis');
  const [filterEmp,    setFilterEmp]    = useState('');
  const [filterCycle,  setFilterCycle]  = useState('');
  const [bonusYear,    setBonusYear]    = useState(2026);
  const [filterRole,   setFilterRole]   = useState('');
  const [saving,       setSaving]       = useState(false);

  // Modals
  const [showTpl,      setShowTpl]      = useState(false);
  const [editTpl,      setEditTpl]      = useState(null);
  const [showAssign,   setShowAssign]   = useState(false);
  const [showBulk,     setShowBulk]     = useState(false);
  const [selfModal,    setSelfModal]    = useState(null);
  const [mgrModal,     setMgrModal]     = useState(null);
  const [bonusModal,   setBonusModal]   = useState(null);
  const [bonusData,    setBonusData]    = useState(null);
  const [devModal,     setDevModal]     = useState(null);

  const defaultRole = roleTypes[0]?.key || '';
  const defaultDept = activeDepartmentId ? String(activeDepartmentId) : '';
  const emptyTpl    = { title:'', description:'', category:'', role_type:defaultRole, cycle_id:'', weight:'', department_id:defaultDept };
  const emptyAssign = { kpi_template_id:'', employee_id:'', cycle_id:'', target:'', weight:10 };
  const emptyBulk   = { employee_id:'', cycle_id:'', role_type:defaultRole };
  const [tplForm,    setTplForm]    = useState(emptyTpl);
  const [assignForm, setAssignForm] = useState(emptyAssign);
  const [bulkForm,   setBulkForm]   = useState(emptyBulk);
  const [selfScores, setSelfScores] = useState({});
  const [mgrScores,  setMgrScores]  = useState({});
  const [devForm,    setDevForm]    = useState({ title:'', category:'', due_date:'', status:'upcoming' });

  const isAdmin    = user?.role === 'admin';
  const isDeptHead = user?.role === 'dept_head';
  const isManager  = ['admin','manager','dept_head'].includes(user?.role);

  const load = useCallback(async () => {
    const params = {};
    if (filterEmp)   params.employee_id = filterEmp;
    if (filterCycle) params.cycle_id    = filterCycle;
    const [t, a] = await Promise.all([getKPITemplates().catch(() => []), getKPIAssignments(params).catch(() => [])]);
    setTemplates(Array.isArray(t) ? t : []); setAssignments(Array.isArray(a) ? a : []);
  }, [filterEmp, filterCycle]);

  const loadChampions = useCallback(async () => {
    if (!isManager) return;
    const ch = await getChampions(bonusYear).catch(()=>[]);
    setChampions(ch);
  }, [bonusYear, isManager]);

  useEffect(() => {
    Promise.all([
      getKPITemplates().catch(() => []),
      getKPIAssignments().catch(() => []),
      getUsers().catch(() => []),
      getCycles().catch(() => []),
      isManager ? getTeamBonus(bonusYear).catch(() => []) : Promise.resolve([]),
      isManager ? getChampions(bonusYear).catch(() => []) : Promise.resolve([]),
      getRoleTypes().catch(() => []),
      getDepartments().catch(() => []),
    ]).then(([t,a,u,c,b,ch,rt,dp]) => {
      setTemplates(Array.isArray(t) ? t : []);
      setAssignments(Array.isArray(a) ? a : []);
      setUsers(Array.isArray(u) ? u : []);
      const cycles = Array.isArray(c) ? c : [];
      setCycles(cycles);
      setTeamBonus(Array.isArray(b) ? b : []);
      setChampions(Array.isArray(ch) ? ch : []);
      setRoleTypes(Array.isArray(rt) ? rt : []);
      setDepartments(Array.isArray(dp) ? dp : []);
      const active = cycles.find(x=>x.status==='active');
      if (active && !filterCycle) setFilterCycle(String(active.id));
    }).finally(()=>setLoading(false));
  }, []);

  useEffect(() => { if (!loading) load(); }, [filterEmp, filterCycle]);

  // roleTypes loads async, so `defaultRole` is '' on the first render and the
  // useState initialisers above capture that empty value permanently. Backfill
  // the role pickers once the list arrives, or the bulk-assign POST ships
  // role_type:'' and the API answers "No templates for role_type:".
  useEffect(() => {
    if (!roleTypes.length) return;
    const first = roleTypes[0].key;
    setBulkForm(f => (f.role_type ? f : { ...f, role_type: first }));
    setTplForm(f => (f.role_type ? f : { ...f, role_type: first }));
  }, [roleTypes]);

  // Load quarterly scores when cycle changes and we're on champion tab
  useEffect(() => {
    if (activeTab === 'champions' && filterCycle && isManager) {
      getQuarterlyScores(filterCycle).then(d => setQuarterlyScores(Array.isArray(d) ? d : [])).catch(()=>setQuarterlyScores([]));
    }
  }, [activeTab, filterCycle]);

  useEffect(() => {
    loadChampions();
    if (isManager) getTeamBonus(bonusYear).then(d => setTeamBonus(Array.isArray(d) ? d : [])).catch(()=>{});
  }, [bonusYear]);

  // ── Template handlers ──────────────────────────────────────────────────
  // Sum of all active template weights for a given (role, cycle), excluding one template (edit case).
  const tplRoleWeight = (roleKey, cycleId, excludeId = null) => templates
    .filter(t => t.role_type === roleKey
      && String(t.cycle_id) === String(cycleId)
      && String(t.id) !== String(excludeId || ''))
    .reduce((s, t) => s + Number(t.weight || 0), 0);

  const tplProposedWeight = Number(tplForm.weight || 0);
  const tplRoleExistingWeight = (tplForm.role_type && tplForm.cycle_id)
    ? tplRoleWeight(tplForm.role_type, tplForm.cycle_id, editTpl?.id)
    : 0;
  const tplWouldExceed = tplForm.role_type && tplForm.cycle_id && (tplRoleExistingWeight + tplProposedWeight > 100.01);
  const tplRemainingCapacity = Math.max(0, 100 - tplRoleExistingWeight);
  // The chosen department filters the selectable role types + cycles (that
  // department's own, plus program-wide). Empty = show all (All departments).
  const tplDept         = tplForm.department_id ? String(tplForm.department_id) : '';
  const tplRoleOptions  = tplDept ? roleTypes.filter(r => String(r.department_id) === tplDept || r.department_id == null) : roleTypes;
  const tplCycleOptions = tplDept ? cycles.filter(c => String(c.department_id) === tplDept || c.department_id == null) : cycles;
  // Only offer departments that have at least one loaded cycle (a template needs
  // a cycle) so the dropdown can't lead to an empty cycle list.
  const tplDeptOptions  = departments.filter(d => cycles.some(c => String(c.department_id) === String(d.id)));
  const tplWeightMissing = tplForm.weight === '' || tplForm.weight === null || Number(tplForm.weight) <= 0;

  const handleSaveTpl = async e => {
    e.preventDefault();
    if (tplWouldExceed) {
      alert(`Total weight for ${tplForm.role_type} templates would exceed 100%. Currently: ${tplRoleExistingWeight.toFixed(1)}% · This template: ${tplProposedWeight}% · Remaining: ${tplRemainingCapacity.toFixed(1)}%.`);
      return;
    }
    setSaving(true);
    try {
      if (editTpl) await updateKPITemplate(editTpl.id, tplForm);
      else await createKPITemplate(tplForm);
      setTemplates(await getKPITemplates()); setShowTpl(false);
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  // ── Bulk assign ────────────────────────────────────────────────────────
  const handleBulkAssign = async e => {
    e.preventDefault(); setSaving(true);
    try {
      const res = await bulkAssignKPIs(bulkForm);
      alert(`Assigned ${res.count} KPIs for ${bulkForm.role_type} role.`);
      await load(); setShowBulk(false);
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  // ── Archive all current KPIs for an employee+cycle (no reassignment) ────
  const handleArchiveSet = async (emp, cycleId) => {
    const activeCount = (emp.items || []).filter(i => !i.archived_at).length;
    if (activeCount === 0) { alert(`${emp.name} has no active KPI assignments to archive in this cycle.`); return; }
    if (!window.confirm(`Archive all ${activeCount} active KPI assignment(s) for ${emp.name} in this cycle?\n\nThis is a soft delete — rows are hidden but kept for audit. The employee will have no KPIs until you either Reassign or use "+ Add KPI".`)) return;
    setSaving(true);
    try {
      const res = await archiveKPISet({ employee_id: emp.id, cycle_id: cycleId });
      alert(`Archived ${res.count} KPI assignment(s) for ${emp.name}.`);
      await load();
    } catch (err) { alert(err.response?.data?.error || 'Error archiving KPIs'); }
    finally { setSaving(false); }
  };

  // ── Reassign: reset to current role-type + cycle + program templates ────
  const handleReassign = async (emp, cycleId) => {
    if (!emp.roleType) {
      alert(`${emp.name} has no role type set. Assign one in Admin → Manage users first.`);
      return;
    }
    const activeCount = (emp.items || []).filter(i => !i.archived_at).length;
    const msg = activeCount > 0
      ? `Reassign ${emp.name}'s KPIs for this cycle?\n\nThis will archive their ${activeCount} current assignment(s) and replace them with the canonical template set for role "${emp.roleType}" in this cycle/program. Existing self-evaluation scores will be cleared.`
      : `Reassign ${emp.name}'s KPIs for this cycle?\n\nThis will assign the canonical template set for role "${emp.roleType}" in this cycle/program.`;
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const res = await reassignKPISet({ employee_id: emp.id, cycle_id: cycleId });
      alert(`Reassigned ${res.assigned_count} KPI(s) for ${emp.name} (role: ${res.role_type}${res.archived_count ? `, archived ${res.archived_count}` : ''}).`);
      await load();
    } catch (err) { alert(err.response?.data?.error || 'Error reassigning KPIs'); }
    finally { setSaving(false); }
  };

  // ── Add single KPI ─────────────────────────────────────────────────────
  const openAddKPI = (empId, cycleId) => {
    setAssignForm({ ...emptyAssign, employee_id: String(empId), cycle_id: String(cycleId) });
    setShowAssign(true);
  };

  // Current total weight already assigned to the targeted employee+cycle (active rows only).
  const assignedWeightTotal = (empId, cycleId) => assignments
    .filter(a => String(a.employee_id) === String(empId) && String(a.cycle_id) === String(cycleId))
    .reduce((s, a) => s + Number(a.weight || 0), 0);

  const assignExistingWeight = assignForm.employee_id && assignForm.cycle_id
    ? assignedWeightTotal(assignForm.employee_id, assignForm.cycle_id)
    : 0;
  const assignProposedWeight = Number(assignForm.weight || 0);
  const assignWouldExceed = assignExistingWeight + assignProposedWeight > 100.01;
  const assignRemaining = Math.max(0, 100 - assignExistingWeight);

  const handleAssignKPI = async e => {
    e.preventDefault();
    if (assignWouldExceed) {
      alert(`Total weight would exceed 100%. Currently assigned: ${assignExistingWeight.toFixed(1)}% · Adding: ${assignProposedWeight}% · Remaining: ${assignRemaining.toFixed(1)}%.`);
      return;
    }
    setSaving(true);
    try {
      await assignKPI(assignForm);
      await load(); setShowAssign(false);
    } catch (err) { alert(err.response?.data?.error || 'Error assigning KPI'); }
    finally { setSaving(false); }
  };

  // ── Archive single KPI ────────────────────────────────────────────────
  const handleArchiveKPI = async (assignmentId, title) => {
    if (!window.confirm(`Archive "${title}" from this employee's KPIs?`)) return;
    setSaving(true);
    try {
      await deleteKPIAssignment(assignmentId);
      await load();
    } catch (err) { alert(err.response?.data?.error || 'Error archiving KPI'); }
    finally { setSaving(false); }
  };

  // ── Self-eval ──────────────────────────────────────────────────────────
  const openSelfEval = (empId, cycleId) => {
    const kpis = assignments.filter(a=>String(a.employee_id)===String(empId)&&String(a.cycle_id)===String(cycleId));
    const init = {};
    kpis.forEach(k=>{ init[k.id]={self_score:k.self_score||0, self_comment:k.self_comment||''}; });
    setSelfScores(init); setSelfModal({employeeId:empId, cycleId, kpis});
  };

  const handleSaveSelf = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await submitSelfEvaluation(selfModal.kpis
        .filter(k => selfScores[k.id]?.self_score > 0)
        .map(k=>({
          assignment_id:k.id, self_score:selfScores[k.id].self_score,
          self_comment:selfScores[k.id].self_comment||'',
        })));
      await load(); setSelfModal(null);
    } catch (err) { alert('Error saving'); }
    finally { setSaving(false); }
  };

  // ── Manager scoring ────────────────────────────────────────────────────
  const openMgrScore = (empId, cycleId) => {
    const kpis = assignments.filter(a=>String(a.employee_id)===String(empId)&&String(a.cycle_id)===String(cycleId));
    const init = {};
    kpis.forEach(k=>{ init[k.id]={score:k.manager_score||0, manager_comment:k.manager_comment||''}; });
    setMgrScores(init); setMgrModal({employeeId:empId, cycleId, kpis});
  };

  const handleSaveMgr = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await submitManagerScores(mgrModal.kpis
        .filter(k => mgrScores[k.id]?.score > 0)
        .map(k=>({
          assignment_id:k.id, score:mgrScores[k.id].score,
          manager_comment:mgrScores[k.id].manager_comment||'', finalise:true,
        })));
      await load(); setMgrModal(null);
    } catch (err) { alert('Error saving'); }
    finally { setSaving(false); }
  };

  // ── Declare champion ───────────────────────────────────────────────────
  const handleDeclareChampion = async (emp) => {
    if (!window.confirm(`Declare ${emp.name} as Quarterly Champion for ${emp.role_type}?\nReward: PHP ${roleReward(emp.role_type).toLocaleString()}`)) return;
    setSaving(true);
    try {
      const res = await declareChampion({ employee_id: emp.employee_id, cycle_id: filterCycle });
      alert(`Champion declared! ${emp.name} — ${res.rating_label} (${res.final_score}/5)\nReward: PHP ${res.reward_php?.toLocaleString()}`);
      await loadChampions();
      const qs = await getQuarterlyScores(filterCycle).catch(()=>[]);
      setQuarterlyScores(qs);
    } catch (err) { alert(err.response?.data?.error || 'Error declaring champion'); }
    finally { setSaving(false); }
  };

  // ── Bonus preview ──────────────────────────────────────────────────────
  const openBonus = async emp => {
    setBonusModal(emp); setBonusData(null);
    const d = await getBonusPreview(emp.employee_id||emp.id, bonusYear).catch(()=>null);
    setBonusData(d);
  };

  const handleGenerateBonus = async () => {
    if (!window.confirm(`Save ${bonusYear} annual bonus for ${bonusModal?.name}?`)) return;
    setSaving(true);
    try {
      await generateBonus({ employee_id:bonusModal.employee_id||bonusModal.id, year:bonusYear });
      const updated = await getBonusPreview(bonusModal.employee_id||bonusModal.id, bonusYear);
      setBonusData({...updated, saved:true});
      setTeamBonus(await getTeamBonus(bonusYear));
    } catch (err) { alert('Error'); }
    finally { setSaving(false); }
  };

  // Grouped by employee for KPI tab
  const byEmployee = assignments.reduce((acc,a)=>{
    const key=a.employee_id;
    if(!acc[key]) acc[key]={id:key,name:a.employee_name,initials:a.avatar_initials,roleType:a.user_role_type||a.role_type,items:[]};
    acc[key].items.push(a);
    return acc;
  },{});

  const allRoleKeys = roleTypes.map(r => r.key);
  const roleLabel = key => roleTypes.find(r => r.key === key)?.label || key;
  const roleBadgeColor = key => roleTypes.find(r => r.key === key)?.badge_color || 'gray';
  const roleReward = key => Number(roleTypes.find(r => r.key === key)?.quarterly_reward || 0);
  const canBeChampion = key => roleTypes.find(r => r.key === key)?.can_be_champion === '1';
  // Templates for the templates-tab view are scoped to the currently selected cycle.
  const visibleTemplates = filterCycle
    ? templates.filter(t => String(t.cycle_id) === String(filterCycle))
    : templates;
  const templatesByRole = allRoleKeys.reduce((acc,r)=>{
    acc[r]=visibleTemplates.filter(t=>t.role_type===r); return acc;
  },{});

  // ── Export visible KPI templates to CSV ──────────────────────────────────
  // Mirrors exactly what the templates tab renders: the cycle-scoped
  // visibleTemplates, further narrowed by the role filter, ordered newest cycle
  // first (like the on-screen groups), then by role/category/title.
  const exportableTemplates = visibleTemplates
    .filter(t => !filterRole || t.role_type === filterRole)
    .slice()
    .sort((a, b) =>
      Number(b.cycle_id) - Number(a.cycle_id)
      || allRoleKeys.indexOf(a.role_type) - allRoleKeys.indexOf(b.role_type)
      || String(a.category || '').localeCompare(String(b.category || ''))
      || String(a.title || '').localeCompare(String(b.title || '')));

  const deptName = id => (id == null || id === '')
    ? 'All departments'
    : (departments.find(d => String(d.id) === String(id))?.name || `#${id}`);

  const exportTemplates = () => {
    if (!exportableTemplates.length) return;
    const headers = ['Cycle', 'Role', 'Category', 'Title', 'Description', 'Weight (%)', 'Department'];
    const rows = exportableTemplates.map(t => [
      t.cycle_name || cycles.find(c => String(c.id) === String(t.cycle_id))?.name || '',
      roleLabel(t.role_type),
      t.category || '',
      t.title || '',
      t.description || '',
      t.weight,
      deptName(t.department_id),
    ]);
    const cyclePart = filterCycle
      ? (cycles.find(c => String(c.id) === String(filterCycle))?.name || 'cycle')
      : 'all-cycles';
    const stamp = new Date().toISOString().slice(0, 10);
    const safe = s => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    downloadCSV(`kpi-templates_${safe(cyclePart)}_${stamp}.csv`, toCSV(headers, rows));
  };

  // Architect objectives — same structure as TechLead but weights flipped:
  // TechArch=60% (primary), FullStack=20%, Strat=5%, Leadership=15%
  const ARCHITECT_OBJECTIVES = [
    { name: 'Strategic Planning & Delivery',      weight: 5,  color: 'amber' },
    { name: 'Technical Architecture & Ownership', weight: 60, color: 'blue'  },
    { name: 'Full-Stack Development',             weight: 20, color: 'green' },
    { name: 'Team Leadership & Mentorship',       weight: 15, color: 'gray'  },
  ];

  // TechLead objectives with weights
  const TECHLEAD_OBJECTIVES = [
    { name: 'Strategic Planning & Delivery',   weight: 5,  color: 'amber' },
    { name: 'Technical Architecture & Ownership', weight: 20, color: 'blue'  },
    { name: 'Full-Stack Development',           weight: 60, color: 'green' },
    { name: 'Team Leadership & Mentorship',     weight: 15, color: 'gray'  },
  ];

  // ── Carry-forward recovery ────────────────────────────────────────────────
  // When the selected cycle has no templates — a cycle created before this
  // feature, or one created with the checkbox unticked — offer to copy from the
  // resolved previous cycle rather than leaving "click + New template" as the
  // only way out of an empty catalogue.
  const runCarryForward = async () => {
    if (!filterCycle) return;
    setCarryBusy(true);
    try {
      const src = await getCarryForwardSource({ cycle_id: Number(filterCycle) });
      if (!src?.source_cycle) {
        alert('No earlier cycle in scope to copy KPIs from.');
        return;
      }
      const cyc = cycles.find(c => String(c.id) === String(filterCycle));
      if (!window.confirm(
        `Copy ${src.templates} KPI${src.templates === 1 ? '' : 's'} from "${src.source_cycle.name}" `
        + `into "${cyc?.name || 'this cycle'}"?\n\n`
        + `${src.people} ${src.people === 1 ? 'person' : 'people'} will get their previous weights.`
        + (src.excluded > 0 ? ` ${src.excluded} previously removed KPI${src.excluded === 1 ? '' : 's'} stay removed.` : '')
      )) return;
      const res = await carryForwardKPIs(src.source_cycle.id, Number(filterCycle));
      const [tpl, asg] = await Promise.all([getKPITemplates(), getKPIAssignments()]);
      setTemplates(Array.isArray(tpl) ? tpl : []);
      setAssignments(Array.isArray(asg) ? asg : []);
      alert(`Copied ${res.templates} KPI${res.templates === 1 ? '' : 's'} from ${res.source_cycle_name}.`);
    } catch (err) {
      alert(err.response?.data?.error || 'Could not copy KPIs.');
    } finally {
      setCarryBusy(false);
    }
  };

  // Diagnose an empty list. Asks about the filtered employee, else self — a
  // per-person answer is the only kind that's useful here.
  useEffect(() => {
    const subject = filterEmp || user?.id;
    if (!subject || assignments.length > 0) { setDiagnosis(null); return; }
    let alive = true;
    diagnoseSetup({ employee_id: Number(subject), ...(filterCycle ? { cycle_id: Number(filterCycle) } : {}) })
      .then(d => { if (alive) setDiagnosis(d); })
      .catch(() => { if (alive) setDiagnosis(null); });
    return () => { alive = false; };
  }, [filterEmp, filterCycle, assignments.length, user?.id]);

  if (loading) return <Spinner />;

  const tabs = mode === 'setup'
    ? [['kpis','Assigned plans'], ['templates','KPI templates']]
    : [['kpis','Scoring'], ...(isManager ? [['champions','Quarterly champions'],['bonus','Annual bonus']] : [])];

  return (
    <div style={{ padding:'20px clamp(16px, 3vw, 32px)' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
        <PageHeader moduleKey={mode==='setup' ? 'kpi-setup' : 'kpi-scoring'} helpTask={mode==='setup' ? 'define-kpi-templates' : 'self-evaluate'} />
        {isManager && mode==='setup' && activeTab==='kpis' && (
          <div style={{ display:'flex', gap:8 }}>
            <Btn variant="secondary" size="sm" onClick={()=>setShowBulk(true)}>Assign KPI set</Btn>
            <Btn size="sm" onClick={()=>{setEditTpl(null);setTplForm({...emptyTpl, cycle_id: filterCycle || String(cycles.find(c=>c.status==='active')?.id || cycles.find(c=>c.cycle_number===2&&c.year===2026)?.id || cycles[0]?.id || '')});setShowTpl(true);}}>+ KPI template</Btn>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:2, borderBottom:'0.5px solid var(--border)', marginBottom:22 }}>
        {tabs.map(([id,label])=>(
          <button key={id} onClick={()=>setActiveTab(id)}
            style={{ padding:'8px 16px', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit',
              fontSize:13, fontWeight:activeTab===id?500:400,
              color:activeTab===id?'var(--navy-600)':'var(--text-3)',
              borderBottom:`2px solid ${activeTab===id?'var(--navy-600)':'transparent'}`, marginBottom:-1 }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── KPI ASSIGNMENTS ── */}
      {activeTab==='kpis' && (
        <div>
          {isManager && mode==='setup' && (
            <div style={{ background:'var(--navy-50)', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12, color:'var(--navy-600)' }}>
              KPIs are auto-assigned: every user with a role type receives their matching templates for each cycle. Use <strong>+ Add KPI</strong> for a single override, <strong>Reassign</strong> to reset a user back to the canonical template set, or <strong>Archive</strong> to clear them entirely. Scoring happens under <strong>Evaluations</strong>.
            </div>
          )}
          {mode==='evaluate' && (
            <div style={{ background:'var(--background-secondary)', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12, color:'var(--text-2)' }}>
              Rate each person 1–5 against the KPIs assigned to them. KPI definitions and weights are read-only here — change them under <strong>KPI setup</strong>.
            </div>
          )}
          <div style={{ display:'flex', gap:10, marginBottom:18, flexWrap:'wrap' }}>
            {isManager && (
              <Select value={filterEmp} onChange={e=>setFilterEmp(e.target.value)} style={{ width:180 }}>
                <option value="">All employees</option>
                {users.map(u=><option key={u.id} value={u.id}>{u.name} ({u.role_type||'—'})</option>)}
              </Select>
            )}
            <Select value={filterCycle} onChange={e=>setFilterCycle(e.target.value)} style={{ width:200 }}>
              <option value="">All cycles</option>
              {cycles.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>

          {Object.values(byEmployee).map(emp=>{
            const cycId = emp.items[0]?.cycle_id;
            const cyc   = cycles.find(c => String(c.id) === String(cycId));
            const cycClosed = cyc?.status === 'closed' && Number(cyc?.program_id) === 1;
            const scored   = emp.items.filter(k=>k.manager_score!=null);
            const selfDone = emp.items.filter(k=>k.self_score!=null);
            const total    = emp.items.length;
            const hasSelf  = total>0 && selfDone.length===total;
            const hasMgr   = scored.length===total && total>0;
            const finalScore = kpiScoreSummary(
              emp.items, k => (k.manager_score != null ? Number(k.manager_score) : null)
            ).finalScore;
            const roleColor  = roleBadgeColor(emp.roleType);
            const isSelf     = String(emp.id)===String(user?.id);
            const weightTotal = emp.items.reduce((s,k)=>s+(Number(k.weight)||0),0);
            const weightOk    = Math.abs(weightTotal-100) <= 0.5;

            // ── SETUP MODE: weighted plan + config actions, no scoring ──
            if (mode==='setup') {
              return (
                <div key={emp.id} style={{ marginBottom:18, background:'var(--surface)', border:'0.5px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 16px' }}>
                    <Link to={`/profile/${emp.id}`} className="user-link" style={{ display:'flex', flexShrink:0, textDecoration:'none' }}>
                      <Avatar initials={emp.initials} size={32} />
                    </Link>
                    <div>
                      <Link to={`/profile/${emp.id}`} className="user-link" style={{ textDecoration:'none' }}>
                        <span className="user-link-name" style={{ fontSize:14, fontWeight:500, color:'var(--text-1)' }}>{emp.name}</span>
                      </Link>
                      {emp.roleType && <Badge color={roleColor} style={{ marginLeft:8 }}>{emp.roleType}</Badge>}
                      <div style={{ fontSize:11, color:'var(--text-3)', marginTop:2 }}>{emp.items[0]?.cycle_name} · {total} KPIs</div>
                    </div>
                    <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
                      <span style={{ fontSize:11.5, fontWeight:600, color: weightOk?'var(--navy-600)':'var(--red-700)' }}>{weightOk?'✓ Balanced':'⚠ Off'} · {weightTotal.toFixed(0)}%</span>
                      {cycClosed && <Badge color="gray">Cycle closed · read-only</Badge>}
                      {!cycClosed && isManager && cycId && <Btn variant="secondary" size="sm" onClick={()=>openAddKPI(emp.id,cycId)}>+ Add KPI</Btn>}
                      {!cycClosed && isManager && cycId && <Btn variant="ghost" size="sm" onClick={()=>handleReassign(emp, cycId)} title="Reset to the canonical template set for this role + cycle">Reassign</Btn>}
                      {!cycClosed && isManager && cycId && <Btn variant="ghost" size="sm" style={{ color:'var(--red-700)' }} onClick={()=>handleArchiveSet(emp, cycId)} title="Archive all KPIs for this cycle">Archive</Btn>}
                    </div>
                  </div>
                  <div style={{ padding:'0 16px 8px' }}>
                    <WeightBar kpis={emp.items} />
                    {!weightOk && (
                      <div style={{ fontSize:11, color:'var(--amber-700)', marginTop:5 }}>
                        Weights across one person's KPIs must total 100% — currently {weightTotal.toFixed(0)}%.
                        Adjust them, or use Reassign to reset to the role's template set.
                      </div>
                    )}
                  </div>
                  <div style={{ padding:'0 8px 10px' }}>
                    {emp.items.map(k=>(
                      <div key={k.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 10px', borderTop:'0.5px solid var(--border)' }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                            <span style={{ fontSize:13, fontWeight:500 }}>{k.title}</span>
                            <Badge color={CAT_COLOR[k.category]||'gray'}>{k.category}</Badge>
                          </div>
                          {k.target && <div style={{ fontSize:11, color:'var(--text-3)', marginTop:3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:640 }}>{k.target}</div>}
                        </div>
                        <span style={{ fontSize:13, fontWeight:600, border:'1px solid var(--border-md)', borderRadius:7, padding:'5px 9px' }}>{k.weight}<span style={{ fontSize:10, color:'var(--text-3)', fontWeight:400 }}>%</span></span>
                        {!cycClosed && isManager && k.workflow_status!=='finalised' && (
                          <button onClick={()=>handleArchiveKPI(k.id,k.title)} title="Remove from this plan"
                            style={{ background:'var(--red-50)', border:'1px solid var(--red-200)', borderRadius:6, cursor:'pointer', width:24, height:24, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:600, color:'var(--red-700)', lineHeight:1 }}>×</button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            // ── EVALUATE MODE: process + one action + compact read-only rows ──
            const phase = cycClosed ? {c:'gray', t:'Cycle closed · read-only'}
              : hasMgr ? {c:'green', t:'Finalised'}
              : hasSelf ? {c:'blue', t:`Ready to score · ${selfDone.length}/${total} self-done`}
              : selfDone.length>0 ? {c:'amber', t:`Self-evaluation in progress · ${selfDone.length}/${total}`}
              : {c:'amber', t:'Awaiting self-evaluation'};
            const phaseColor = phase.c==='green'?'var(--navy-600)':phase.c==='blue'?'var(--blue-700)':phase.c==='amber'?'var(--amber-700)':'var(--text-3)';
            const stepIdx = (cycClosed||hasMgr) ? 3 : hasSelf ? 2 : 1;
            const only = Object.keys(byEmployee).length===1;
            const open = only || openEmp===emp.id;

            return (
              <div key={emp.id} style={{ marginBottom:12, background:'var(--surface)', border:`0.5px solid ${open?'var(--border-md)':'var(--border)'}`, borderRadius:12, overflow:'hidden', boxShadow: open?'0 6px 18px -12px rgba(0,0,0,0.12)':'none' }}>
                <div onClick={()=>setOpenEmp(cur=>cur===emp.id?null:emp.id)} style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 16px', cursor: only?'default':'pointer' }}>
                  <Link to={`/profile/${emp.id}`} onClick={e=>e.stopPropagation()} className="user-link" style={{ display:'flex', flexShrink:0, textDecoration:'none' }}>
                    <Avatar initials={emp.initials} size={32} />
                  </Link>
                  <div style={{ minWidth:0 }}>
                    <Link to={`/profile/${emp.id}`} onClick={e=>e.stopPropagation()} className="user-link" style={{ textDecoration:'none' }}>
                      <span className="user-link-name" style={{ fontSize:14, fontWeight:500, color:'var(--text-1)' }}>{emp.name}</span>
                    </Link>
                    {emp.roleType && <Badge color={roleColor} style={{ marginLeft:8 }}>{emp.roleType}</Badge>}
                    <div style={{ fontSize:11, color:'var(--text-3)', marginTop:2 }}>{emp.items[0]?.cycle_name} · {total} KPIs</div>
                  </div>
                  <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:14 }}>
                    {finalScore!=null
                      ? <div style={{ textAlign:'right' }}><span style={{ fontSize:20, fontWeight:600, color:scoreColor(finalScore) }}>{finalScore.toFixed(2)}</span><span style={{ fontSize:11, color:'var(--text-3)' }}>/5 · {scoreToLabel(finalScore)}</span></div>
                      : <span style={{ display:'inline-flex', alignItems:'center', gap:7, fontSize:12, color:phaseColor }}><span style={{ width:8, height:8, borderRadius:'50%', background:phaseColor }} />{phase.t}</span>}
                    {!only && <span style={{ color:'var(--text-3)', fontSize:14, transform: open?'rotate(180deg)':'none', transition:'transform .18s', display:'inline-block' }}>⌄</span>}
                  </div>
                </div>

                {open && (<>
                  <div style={{ display:'flex', alignItems:'center', gap:18, padding:'11px 16px', background:'var(--background-secondary)', borderTop:'0.5px solid var(--border)', borderBottom:'0.5px solid var(--border)', flexWrap:'wrap' }}>
                    {[['1','Self-evaluation'],['2','Manager scoring'],['3','Finalised']].map(([n,label])=>{
                      const st = Number(n)<stepIdx?'done':Number(n)===stepIdx?'now':'todo';
                      const col = st==='done'?'var(--navy-600)':st==='now'?'var(--amber-700)':'var(--text-3)';
                      return (
                        <span key={n} style={{ display:'flex', alignItems:'center', gap:7, fontSize:12, color:col, fontWeight:st==='now'?600:400 }}>
                          <span style={{ width:19, height:19, borderRadius:'50%', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:600, color:st==='todo'?'var(--text-3)':'#fff', background:st==='done'?'var(--navy-600)':st==='now'?'var(--amber-700)':'transparent', border:st==='todo'?'1.5px solid var(--border-md)':'none' }}>{st==='done'?'✓':n}</span>
                          {label}
                        </span>
                      );
                    })}
                    {!cycClosed && !hasMgr && <span style={{ fontSize:12, color:'var(--text-2)', marginLeft:'auto' }}>{selfDone.length} of {total} self-evaluated</span>}
                  </div>

                  {!cycClosed && (
                    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 16px' }}>
                      <span style={{ fontSize:12, color:'var(--text-3)' }}>{hasMgr ? 'Scored and finalised.' : hasSelf ? (isManager?'Ready for your review.':'Submitted — awaiting your manager.') : 'Rate each KPI 1–5 against its target.'}</span>
                      <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
                        {isManager && hasSelf && !hasMgr && <Btn size="sm" onClick={()=>openMgrScore(emp.id,cycId)}>Score KPIs</Btn>}
                        {isManager && hasMgr && <Btn variant="secondary" size="sm" onClick={()=>openMgrScore(emp.id,cycId)}>Edit scores</Btn>}
                        {(isSelf||isManager) && !hasMgr && <Btn variant={isManager&&hasSelf?'ghost':'primary'} size="sm" onClick={()=>openSelfEval(emp.id,cycId)}>{hasSelf?'Edit self-eval':selfDone.length>0?'Continue self-evaluation':'Start self-evaluation'}</Btn>}
                        {isManager && hasMgr && <Btn variant="ghost" size="sm" onClick={()=>{setDevModal({employeeId:emp.id,name:emp.name});setDevForm({title:'',category:'',due_date:'',status:'upcoming'});}}>+ Dev plan</Btn>}
                        {isManager && cycId && <button onClick={()=>navigate('/kpi-setup')} style={{ background:'none', border:'none', cursor:'pointer', fontSize:11.5, color:'var(--blue-700)', fontWeight:500 }}>Manage plan →</button>}
                      </div>
                    </div>
                  )}

                  <div style={{ padding:'0 8px 12px' }}>
                    {emp.items.map(k=>{
                      // manager_score is DECIMAL(4,2), so it arrives as "4.00" — keying
                      // KPI_SCORE_LABELS with it directly rendered "4.00 · undefined".
                      const selfS = k.self_score != null && k.self_score !== '' ? Number(k.self_score) : null;
                      const mgrS  = k.manager_score != null && k.manager_score !== '' ? Number(k.manager_score) : null;
                      const stat = k.workflow_status==='finalised'?['green','Finalised']:selfS?['blue','Awaiting manager']:['amber','Awaiting self-eval'];
                      return (
                        <div key={k.id} style={{ padding:'11px 10px', borderTop:'0.5px solid var(--border)' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                                <span style={{ fontSize:13, fontWeight:500 }}>{k.title}</span>
                                <Badge color={CAT_COLOR[k.category]||'gray'}>{k.category}</Badge>
                                <span style={{ fontSize:11, color:'var(--text-3)', fontWeight:500 }}>{k.weight}%</span>
                              </div>
                              {/* The target is what the rating is against, so it wraps in
                                  full here rather than being cut off at one line. */}
                              {k.target && <div style={{ fontSize:11, color:'var(--text-3)', marginTop:3, lineHeight:1.5, whiteSpace:'pre-wrap' }}>{k.target}</div>}
                            </div>
                            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                              {(selfS!=null||mgrS!=null) && (
                                <div style={{ display:'flex', gap:12 }}>
                                  <span style={{ fontSize:11, color:'var(--text-2)' }}><span style={{ fontSize:9, textTransform:'uppercase', color:'var(--text-3)', marginRight:4 }}>Self</span><b>{selfS!=null?`${selfS} · ${KPI_SCORE_LABELS[Math.round(selfS)]}`:'—'}</b></span>
                                  <span style={{ fontSize:11, color:'var(--text-2)' }}><span style={{ fontSize:9, textTransform:'uppercase', color:'var(--text-3)', marginRight:4 }}>Mgr</span><b>{mgrS!=null?`${mgrS} · ${KPI_SCORE_LABELS[Math.round(mgrS)]}`:'—'}</b></span>
                                </div>
                              )}
                              <Badge color={stat[0]}>{stat[1]}</Badge>
                            </div>
                          </div>
                          {/* The reasoning behind each rating. The employee's only
                              route to their manager's per-KPI comment — the scoring
                              modal that holds it is manager-only. */}
                          <ScoreNote label={isSelf ? 'Your self-evaluation' : 'Self-evaluation'}
                            score={selfS} comment={k.self_comment} when={k.self_submitted_at} />
                          <ScoreNote label="Manager's score" who={k.scored_by_name}
                            score={mgrS} comment={k.manager_comment} when={k.scored_at} />
                        </div>
                      );
                    })}
                  </div>

                  {finalScore!=null && (
                    <div style={{ margin:'0 16px 16px', background:CALIBRATION_MAP[scoreToCalibration(finalScore)]?.bg, border:'0.5px solid', borderColor:CALIBRATION_MAP[scoreToCalibration(finalScore)]?.color+'33', borderRadius:8, padding:'11px 15px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div>
                        <div style={{ fontSize:12, fontWeight:500, color:scoreColor(finalScore) }}>Final score · {scoreToLabel(finalScore)}</div>
                        <div style={{ fontSize:10, color:scoreColor(finalScore) }}>Annual bonus: {CALIBRATION_MAP[scoreToCalibration(finalScore)]?.bonusPct}% of monthly salary{emp.roleType && roleReward(emp.roleType)>0 && ` · Champion reward: PHP ${roleReward(emp.roleType).toLocaleString()}`}</div>
                      </div>
                      <div style={{ textAlign:'right' }}><span style={{ fontSize:26, fontWeight:500, color:scoreColor(finalScore) }}>{finalScore.toFixed(2)}</span><span style={{ fontSize:11, color:'var(--text-3)' }}>/5</span></div>
                    </div>
                  )}
                </>)}
              </div>
            );
          })}

          {Object.keys(byEmployee).length===0 && (
            <div style={{ textAlign:'center', padding:'40px 16px', color:'var(--text-3)' }}>
              <div style={{ fontSize:13, fontWeight:500, color:'var(--text-2)' }}>No KPIs here yet</div>
              {/* The diagnosis names the exact missing step instead of leaving
                  people to guess between six possible causes. */}
              {diagnosis?.blocked ? (
                <>
                  <div style={{ fontSize:12.5, color:'var(--amber-700)', marginTop:8, maxWidth:520, marginLeft:'auto', marginRight:'auto', lineHeight:1.6 }}>
                    {diagnosis.message}
                  </div>
                  <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:12, flexWrap:'wrap' }}>
                    {diagnosis.task_id && (
                      <Link to={`/guide#task-${diagnosis.task_id}`} style={{ textDecoration:'none' }}>
                        <Btn size="sm" variant="secondary">How to fix this</Btn>
                      </Link>
                    )}
                    {/* The common case — no templates for this cycle — is one click
                        away thanks to carry-forward. */}
                    {diagnosis.reason === 'no_templates' && filterCycle && isManager && (
                      <Btn size="sm" disabled={carryBusy} onClick={runCarryForward}>
                        {carryBusy ? 'Copying…' : 'Copy KPIs from previous cycle'}
                      </Btn>
                    )}
                  </div>
                </>
              ) : (
                <div style={{ fontSize:12.5, marginTop:8 }}>
                  {mode==='setup' ? 'Use "Assign KPI set" to assign a role template to someone.' : (isManager ? 'Pick an employee or cycle above.' : 'Your manager will assign KPIs to you.')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── TEMPLATES ── */}
      {activeTab==='templates' && (
        <div>
          <div style={{ background:'var(--blue-50)', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12, color:'var(--blue-700)' }}>
            Templates are scoped to a role type and a review cycle. Creating or editing a template auto-assigns it to every active user whose role type matches, for the selected cycle.
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:10, alignItems:'center', marginBottom:14 }}>
            <span style={{ fontSize:12, color:'var(--text-3)', fontWeight:500 }}>Cycle:</span>
            <Select value={filterCycle} onChange={e=>setFilterCycle(e.target.value)} style={{ width:220 }}>
              <option value="">All cycles</option>
              {cycles.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <span style={{ fontSize:12, color:'var(--text-3)', fontWeight:500, marginLeft:8 }}>Role:</span>
            <select value={filterRole} onChange={e=>setFilterRole(e.target.value)}
              style={{ padding:'7px 12px', fontSize:13, border:'0.5px solid var(--border-md)', borderRadius:'var(--radius-md)', background:'var(--surface)', color:'var(--text-1)', minWidth:180 }}>
              <option value="">All roles</option>
              {allRoleKeys.map(r=><option key={r} value={r}>{roleLabel(r)}</option>)}
            </select>
            <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
              <Btn variant="secondary" size="sm" disabled={!exportableTemplates.length} onClick={exportTemplates}
                title={exportableTemplates.length ? `Download ${exportableTemplates.length} KPI template${exportableTemplates.length>1?'s':''} as CSV` : 'No templates to export'}>
                <DownloadIcon /> Export
              </Btn>
              <Btn size="sm" onClick={()=>{setEditTpl(null);setTplForm({...emptyTpl, cycle_id: filterCycle || String(cycles.find(c=>c.status==='active')?.id || cycles.find(c=>c.cycle_number===2&&c.year===2026)?.id || cycles[0]?.id || '')});setShowTpl(true);}}>+ New template</Btn>
            </div>
          </div>
          {(() => {
            // Weight math is per (role, cycle) — so even when "All cycles" is
            // selected, group by cycle first so the per-group weight sums stay
            // meaningful. When filterCycle is set, visibleTemplates is already
            // scoped to that cycle; the outer loop then has a single iteration.
            const cycleIds = filterCycle
              ? [filterCycle]
              : [...new Set(visibleTemplates.map(t => String(t.cycle_id)))]
                  .sort((a,b) => Number(b) - Number(a));
            if (cycleIds.length === 0) {
              return (
                <div style={{ padding:'20px 16px', textAlign:'center', fontSize:13, color:'var(--text-3)', background:'var(--gray-50)', borderRadius:8 }}>
                  No KPI templates {filterRole ? `for ${roleLabel(filterRole)} ` : ''}yet.
                  {' '}Click <strong>+ New template</strong> to add one{filterCycle ? ', or bring last cycle\u2019s across' : ''}.
                  {filterCycle && isManager && (
                    <div style={{ marginTop:10 }}>
                      <Btn size="sm" variant="secondary" disabled={carryBusy} onClick={runCarryForward}>
                        {carryBusy ? 'Copying\u2026' : 'Copy KPIs from previous cycle'}
                      </Btn>
                    </div>
                  )}
                </div>
              );
            }
            return cycleIds.map(cid => {
              const cycle = cycles.find(c => String(c.id) === String(cid));
              const cycleName = cycle?.name || 'Unassigned cycle';
              const cycleTpls = visibleTemplates.filter(t => String(t.cycle_id) === String(cid));
              const cycleRoles = allRoleKeys.filter(r => cycleTpls.some(t => t.role_type === r));
              const shownRoles = cycleRoles.filter(role=>!filterRole||role===filterRole);
              return (
                <div key={cid} style={{ marginBottom:26 }}>
                  {!filterCycle && (
                    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', marginBottom:12, background:'var(--gray-100)', borderRadius:8, borderLeft:'3px solid var(--navy-600)' }}>
                      <span style={{ fontSize:13, fontWeight:500 }}>{cycleName}</span>
                    </div>
                  )}
                  {shownRoles.length === 0 && (
                    <div style={{ padding:'20px 16px', textAlign:'center', fontSize:13, color:'var(--text-3)', background:'var(--gray-50)', borderRadius:8 }}>
                      No KPI templates {filterRole ? `for ${roleLabel(filterRole)} ` : ''}in {filterCycle ? 'this cycle' : cycleName} yet.
                      {' '}Click <strong>+ New template</strong> to add one{filterCycle ? ', or bring last cycle\u2019s across' : ''}.
                      {filterCycle && isManager && (
                        <div style={{ marginTop:10 }}>
                          <Btn size="sm" variant="secondary" disabled={carryBusy} onClick={runCarryForward}>
                            {carryBusy ? 'Copying\u2026' : 'Copy KPIs from previous cycle'}
                          </Btn>
                        </div>
                      )}
                    </div>
                  )}
                  {shownRoles.map(role=>{
            const roleTpls = cycleTpls.filter(t => t.role_type === role);
            if (!roleTpls.length) return null;

            const categories = [...new Set(roleTpls.map(t=>t.category || 'Uncategorized'))];

            return (
              <div key={role} style={{ marginBottom:22 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                  <SectionTitle>{roleLabel(role)} KPIs</SectionTitle>
                  <Badge color={roleBadgeColor(role)}>{roleLabel(role)}</Badge>
                  <span style={{ fontSize:11, color:'var(--text-3)' }}>({roleTpls.length} KPIs · weights: {roleTpls.reduce((s,t)=>s+Number(t.weight),0).toFixed(0)}%)</span>
                </div>
                {categories.length > 1 ? categories.map(cat => {
                  const catTpls = roleTpls.filter(t=>(t.category || 'Uncategorized')===cat);
                  const catWeight = catTpls.reduce((s,t)=>s+Number(t.weight),0);
                  return (
                    <div key={cat} style={{ marginBottom:14 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, padding:'7px 12px', background:'var(--gray-100)', borderRadius:8, borderLeft:'3px solid var(--navy-500)' }}>
                        <span style={{ fontSize:12, fontWeight:500 }}>{cat}</span>
                        <Badge color="green">{catWeight.toFixed(0)}% weight</Badge>
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(2,minmax(0,1fr))', gap:8 }}>
                        {catTpls.map(t=>(
                          <Card key={t.id}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
                              <div style={{ flex:1 }}>
                                <div style={{ fontWeight:500, fontSize:12 }}>{t.title}</div>
                                <div style={{ fontSize:10, color:'var(--text-3)' }}>{t.weight}% weight</div>
                              </div>
                              {isManager && (
                                <div style={{ display:'flex', gap:4, marginLeft:8 }}>
                                  <Btn variant="ghost" size="sm" onClick={()=>{setEditTpl(t);setTplForm({title:t.title,description:t.description||'',category:t.category||'',role_type:t.role_type||'',cycle_id:String(t.cycle_id||''),weight:t.weight||'',department_id:t.department_id?String(t.department_id):''});setShowTpl(true);}} style={{ padding:'2px 6px' }}>✎</Btn>
                                  <Btn variant="ghost" size="sm" onClick={()=>{if(window.confirm('Archive this KPI template?'))deleteKPITemplate(t.id).then(()=>getKPITemplates().then(d=>setTemplates(Array.isArray(d)?d:[])))}} style={{ padding:'2px 6px', color:'var(--red-500)' }}>✕</Btn>
                                </div>
                              )}
                            </div>
                            {t.description && <div style={{ fontSize:11, color:'var(--text-3)', lineHeight:1.5 }}>{t.description}</div>}
                          </Card>
                        ))}
                      </div>
                    </div>
                  );
                }) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(2,minmax(0,1fr))', gap:8 }}>
                  {roleTpls.map(t=>(
                    <Card key={t.id}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:500, fontSize:12 }}>{t.title}</div>
                          <div style={{ fontSize:10, color:'var(--text-3)' }}>{t.category} · {t.weight}% weight</div>
                        </div>
                        {isManager && (
                          <div style={{ display:'flex', gap:4, marginLeft:8 }}>
                            <Btn variant="ghost" size="sm" onClick={()=>{setEditTpl(t);setTplForm({title:t.title,description:t.description||'',category:t.category||'',role_type:t.role_type||'',cycle_id:String(t.cycle_id||''),weight:t.weight||'',department_id:t.department_id?String(t.department_id):''});setShowTpl(true);}} style={{ padding:'2px 6px' }}>✎</Btn>
                            <Btn variant="ghost" size="sm" onClick={()=>{if(window.confirm('Archive this KPI template?'))deleteKPITemplate(t.id).then(()=>getKPITemplates().then(d=>setTemplates(Array.isArray(d)?d:[])))}} style={{ padding:'2px 6px', color:'var(--red-500)' }}>✕</Btn>
                          </div>
                        )}
                      </div>
                      {t.description && <div style={{ fontSize:11, color:'var(--text-3)', lineHeight:1.5 }}>{t.description}</div>}
                    </Card>
                  ))}
                </div>
                )}
              </div>
            );
          })}
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* ── QUARTERLY CHAMPIONS ── */}
      {activeTab==='champions' && isManager && (
        <div>
          <div style={{ background:'#FFF7D6', border:'0.5px solid #FDE9A3', borderRadius:8, padding:'11px 15px', marginBottom:20, fontSize:13, color:'#7A5E00' }}>
            Quarterly champions are announced at team meetings and via email. Rewards: <strong>Developer Champion PHP 10,000</strong> · <strong>QA Champion PHP 5,000</strong> per cycle. Total budget: PHP 45,000/year.
          </div>

          {/* Cycle filter */}
          <div style={{ display:'flex', gap:10, marginBottom:20, alignItems:'center' }}>
            <span style={{ fontSize:12, color:'var(--text-3)' }}>Cycle:</span>
            <Select value={filterCycle} onChange={async e=>{setFilterCycle(e.target.value);const qs=await getQuarterlyScores(e.target.value).catch(()=>[]);setQuarterlyScores(qs);}} style={{ width:220 }}>
              <option value="">Select cycle…</option>
              {cycles.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>

          {/* Past champions */}
          {champions.length>0 && (
            <>
              <SectionTitle>Declared champions · {bonusYear}</SectionTitle>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,minmax(0,1fr))', gap:10, marginBottom:20 }}>
                {champions.map(ch=>(
                  <Card key={ch.id} style={{ background:'#FFF7D6', border:'0.5px solid #C4D0E6' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                      <Avatar initials={ch.avatar_initials} size={36} />
                      <div>
                        <div style={{ fontSize:13, fontWeight:500 }}>{ch.name}</div>
                        <div style={{ fontSize:11, color:'var(--text-3)' }}>{ch.cycle_name}</div>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:6 }}>
                      <Badge color={roleBadgeColor(ch.role_type)}>{ch.role_type} champion</Badge>
                      <RatingBadge score={ch.final_score} />
                    </div>
                    <div style={{ fontSize:13, fontWeight:500, color:'#7A5E00' }}>
                      {ch.final_score}/5 · PHP {Number(ch.reward_php).toLocaleString()}
                    </div>
                  </Card>
                ))}
              </div>
            </>
          )}

          {/* This cycle scores */}
          {quarterlyScores.length>0 && (
            <>
              <SectionTitle>Scores this cycle</SectionTitle>
              <Card style={{ padding:0, overflow:'hidden', marginTop:8 }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ background:'var(--gray-50)' }}>
                      {['Name','Role','Final score','Rating','KPIs scored','Quarterly reward','Champion'].map(h=>(
                        <th key={h} style={{ padding:'9px 13px', textAlign:'left', fontSize:10, textTransform:'uppercase', letterSpacing:'0.5px', color:'var(--text-3)', borderBottom:'0.5px solid var(--border)', fontWeight:500 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {quarterlyScores.map(emp=>(
                      <tr key={emp.employee_id} style={{ borderBottom:'0.5px solid var(--border)' }}>
                        <td style={{ padding:'10px 13px' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                            <Avatar initials={emp.avatar_initials} size={26} />
                            <div>
                              <div style={{ fontWeight:500 }}>{emp.name}</div>
                              <div style={{ fontSize:10, color:'var(--text-3)' }}>{emp.job_title}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding:'10px 13px' }}><Badge color={roleBadgeColor(emp.role_type)}>{emp.role_type||'—'}</Badge></td>
                        <td style={{ padding:'10px 13px' }}>
                          {emp.final_score!=null
                            ? <><span style={{ fontSize:16, fontWeight:500, color:scoreColor(emp.final_score) }}>{emp.final_score}</span><span style={{ fontSize:10, color:'var(--text-3)' }}>/5</span></>
                            : <span style={{ color:'var(--text-3)' }}>{emp.scored_count}/{emp.kpi_count} scored</span>}
                        </td>
                        <td style={{ padding:'10px 13px' }}>
                          {emp.final_score!=null ? <RatingBadge score={emp.final_score} /> : <span style={{ color:'var(--text-3)', fontSize:11 }}>Incomplete</span>}
                        </td>
                        <td style={{ padding:'10px 13px', color:'var(--text-3)' }}>{emp.scored_count}/{emp.kpi_count}</td>
                        <td style={{ padding:'10px 13px', fontWeight:500, color:'#7A5E00' }}>
                          {emp.quarterly_reward>0 ? `PHP ${emp.quarterly_reward.toLocaleString()}` : '—'}
                        </td>
                        <td style={{ padding:'10px 13px' }}>
                          {emp.is_champion
                            ? <Badge color="green">Champion</Badge>
                            : (isAdmin || isDeptHead) && canBeChampion(emp.role_type)
                              ? <Btn size="sm" variant="secondary" onClick={()=>handleDeclareChampion(emp)} disabled={saving}
                                  style={{ fontSize:11, padding:'4px 10px', background:'#FFF7D6', border:'1px solid #C4D0E6', color:'#7A5E00' }}>
                                  Declare
                                </Btn>
                              : <span style={{ color:'var(--text-3)', fontSize:11 }}>—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </>
          )}

          {quarterlyScores.length===0 && filterCycle && (
            <div style={{ textAlign:'center', padding:'48px 0', color:'var(--text-3)' }}>No scored KPIs for this cycle yet.</div>
          )}
        </div>
      )}

      {/* ── ANNUAL BONUS ── */}
      {activeTab==='bonus' && isManager && (
        <div>
          <div style={{ background:'#FFF7D6', border:'0.5px solid #FDE9A3', borderRadius:8, padding:'11px 15px', marginBottom:20 }}>
            <div style={{ fontSize:13, color:'#7A5E00', fontWeight:500, marginBottom:6 }}>Annual bonus = average of all 3 quarterly KPI scores · paid as % of one monthly salary</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:6, fontSize:11 }}>
              {[['4.5–5.0','Outstanding','100%'],['4.0–4.49','Excellent','75%'],['3.5–3.99','Strong Performance','50%'],['3.0–3.49','Meets Expectations','25%'],['< 3.0','Needs Improvement','0%']].map(([r,l,b])=>(
                <div key={r} style={{ background:'rgba(255,255,255,0.6)', borderRadius:6, padding:'6px 8px', textAlign:'center' }}>
                  <div style={{ fontWeight:500, color:'#7A5E00' }}>{b}</div>
                  <div style={{ color:'#7A5E00', fontSize:10 }}>{l}</div>
                  <div style={{ color:'#1F3B68', fontSize:9 }}>{r}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:18 }}>
            <span style={{ fontSize:12, color:'var(--text-3)' }}>Year:</span>
            <Select value={bonusYear} onChange={e=>setBonusYear(Number(e.target.value))} style={{ width:90 }}>
              {[2026,2025].map(y=><option key={y} value={y}>{y}</option>)}
            </Select>
          </div>

          {teamBonus.length>0 && (
            <Card style={{ padding:0, overflow:'hidden', marginBottom:20 }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ background:'var(--gray-50)' }}>
                    {['Employee','Role','Avg KPI score','Rating','Bonus (% monthly salary)',''].map(h=>(
                      <th key={h} style={{ padding:'9px 13px', textAlign:'left', fontSize:10, textTransform:'uppercase', letterSpacing:'0.5px', color:'var(--text-3)', borderBottom:'0.5px solid var(--border)', fontWeight:500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {teamBonus.map(b=>(
                    <tr key={b.employee_id} style={{ borderBottom:'0.5px solid var(--border)' }}>
                      <td style={{ padding:'10px 13px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                          <Avatar initials={b.avatar_initials} size={26} />
                          <div><div style={{ fontWeight:500 }}>{b.name}</div><div style={{ fontSize:10, color:'var(--text-3)' }}>{b.job_title}</div></div>
                        </div>
                      </td>
                      <td style={{ padding:'10px 13px' }}><Badge color={roleBadgeColor(b.role_type)}>{b.role_type||'—'}</Badge></td>
                      <td style={{ padding:'10px 13px', fontWeight:500, color:scoreColor(b.combined_score) }}>{b.combined_score??'—'}</td>
                      <td style={{ padding:'10px 13px' }}>{b.combined_score!=null?<RatingBadge score={b.combined_score} />:<span style={{ color:'var(--text-3)' }}>—</span>}</td>
                      <td style={{ padding:'10px 13px', fontSize:16, fontWeight:500, color:scoreColor(b.combined_score) }}>
                        {b.bonus_pct_monthly!=null?`${b.bonus_pct_monthly}%`:'—'}
                      </td>
                      <td style={{ padding:'10px 13px' }}>
                        <Btn variant="secondary" size="sm" onClick={()=>openBonus(b)}>View</Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          <SectionTitle>Generate annual bonus</SectionTitle>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,minmax(0,1fr))', gap:10, marginTop:10 }}>
            {users.map(u=>(
              <Card key={u.id} style={{ cursor:'pointer', padding:'12px 14px' }} onClick={()=>openBonus(u)}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <Avatar initials={u.avatar_initials} size={28} />
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:12, fontWeight:500 }}>{u.name}</div>
                    <div style={{ fontSize:10, color:'var(--text-3)' }}>{u.role_type||u.job_title}</div>
                  </div>
                  <span style={{ marginLeft:'auto', fontSize:11, color:'var(--navy-600)' }}>→</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ── MODALS ── */}

      {/* Template form */}
      <Modal open={showTpl} onClose={()=>setShowTpl(false)} title={editTpl?'Edit KPI template':'New KPI template'} width={520}>
        <form onSubmit={handleSaveTpl}>
          <div style={{ background:'var(--blue-50)', borderRadius:8, padding:'9px 12px', marginBottom:14, fontSize:12, color:'var(--blue-700)' }}>
            Saving this template auto-assigns it to every active user with a matching role type for the selected cycle.
          </div>
          <Field label="Title" required><Input value={tplForm.title} onChange={e=>setTplForm(f=>({...f,title:e.target.value}))} placeholder="e.g. On-time Task Delivery" /></Field>
          <Field label="Description"><Textarea value={tplForm.description} onChange={e=>setTplForm(f=>({...f,description:e.target.value}))} rows={2} /></Field>
          <Field label="Department">
            <Select value={tplForm.department_id}
              onChange={e=>setTplForm(f=>({...f, department_id:e.target.value, role_type:'', cycle_id:''}))}>
              <option value="">All departments (program-wide)</option>
              {tplDeptOptions.map(d=><option key={d.id} value={d.id}>{d.name}{d.program_name?` · ${d.program_name}`:''}</option>)}
            </Select>
          </Field>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Field label="Role type" required>
              <Select value={tplForm.role_type} onChange={e=>setTplForm(f=>({...f,role_type:e.target.value}))}>
                <option value="">Select role type…</option>
                {tplRoleOptions.map(r=><option key={r.key} value={r.key}>{r.label}</option>)}
              </Select>
            </Field>
            <Field label="Review cycle" required>
              <Select value={tplForm.cycle_id} onChange={e=>setTplForm(f=>({...f,cycle_id:e.target.value}))}>
                <option value="">Select cycle…</option>
                {tplCycleOptions.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Field label="Category" required><Input value={tplForm.category} onChange={e=>setTplForm(f=>({...f,category:e.target.value}))} placeholder="e.g. Delivery" required /></Field>
            <Field label="Weight (%)" required><Input type="number" step="0.5" min="0" max="100" value={tplForm.weight} onChange={e=>setTplForm(f=>({...f,weight:e.target.value}))} placeholder="e.g. 10" required /></Field>
          </div>
          {tplForm.role_type && (
            <div style={{ fontSize:11, color:tplWouldExceed?'var(--red-700)':'var(--text-3)', marginBottom:12 }}>
              {tplForm.role_type} templates currently: <strong>{tplRoleExistingWeight.toFixed(1)}%</strong>
              {' · '}remaining capacity: <strong>{tplRemainingCapacity.toFixed(1)}%</strong>
              {tplWouldExceed && <span style={{ fontWeight:500 }}> · exceeds 100% — reduce weight before saving.</span>}
            </div>
          )}
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn type="button" variant="secondary" onClick={()=>setShowTpl(false)}>Cancel</Btn>
            <Btn type="submit" disabled={saving||!tplForm.title||!tplForm.category||!tplForm.role_type||!tplForm.cycle_id||tplWeightMissing||tplWouldExceed}>{saving?'Saving…':editTpl?'Save':'Create'}</Btn>
          </div>
        </form>
      </Modal>

      {/* Add single KPI */}
      <Modal open={showAssign} onClose={()=>setShowAssign(false)} title="Add KPI to employee" width={460}>
        <form onSubmit={handleAssignKPI}>
          <Field label="KPI template" required>
            <Select value={assignForm.kpi_template_id} onChange={e=>{ const t=templates.find(x=>String(x.id)===e.target.value); setAssignForm(f=>({...f, kpi_template_id:e.target.value, target:t?.description||f.target})); }}>
              <option value="">Select KPI…</option>
              {allRoleKeys.map(role => {
                const roleTpls = templates.filter(t=>t.role_type===role);
                return roleTpls.length > 0 ? <optgroup key={role} label={roleLabel(role)}>{roleTpls.map(t=><option key={t.id} value={t.id}>{t.title} ({t.category})</option>)}</optgroup> : null;
              })}
            </Select>
          </Field>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Field label="Weight (%)">
              <Input type="number" step="0.5" min="0.5" max="100" value={assignForm.weight} onChange={e=>setAssignForm(f=>({...f,weight:e.target.value}))} />
            </Field>
            <Field label="Employee">
              <Select value={assignForm.employee_id} onChange={e=>setAssignForm(f=>({...f,employee_id:e.target.value}))}>
                <option value="">Select…</option>
                {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
              </Select>
            </Field>
          </div>
          {assignForm.employee_id && assignForm.cycle_id && (
            <div style={{ fontSize:11, color:assignWouldExceed?'var(--red-700)':'var(--text-3)', marginBottom:12 }}>
              Employee already has <strong>{assignExistingWeight.toFixed(1)}%</strong> assigned this cycle · remaining capacity: <strong>{assignRemaining.toFixed(1)}%</strong>
              {assignWouldExceed && <span style={{ fontWeight:500 }}> · total would exceed 100% — reduce the weight.</span>}
            </div>
          )}
          <Field label="Target / success criteria">
            <Textarea value={assignForm.target} onChange={e=>setAssignForm(f=>({...f,target:e.target.value}))} rows={2} placeholder="What does success look like for this KPI?" />
          </Field>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn type="button" variant="secondary" onClick={()=>setShowAssign(false)}>Cancel</Btn>
            <Btn type="submit" disabled={saving||!assignForm.kpi_template_id||!assignForm.employee_id||assignWouldExceed}>{saving?'Adding…':'Add KPI'}</Btn>
          </div>
        </form>
      </Modal>

      {/* Bulk assign */}
      <Modal open={showBulk} onClose={()=>setShowBulk(false)} title="Assign KPI set to employee" width={420}>
        <form onSubmit={handleBulkAssign}>
          <div style={{ background:'var(--blue-50)', borderRadius:8, padding:'9px 12px', marginBottom:14, fontSize:12, color:'var(--blue-700)' }}>
            This will assign all KPIs for the selected role type to the employee, replacing any existing assignments for this cycle.
          </div>
          <Field label="Employee" required>
            <Select value={bulkForm.employee_id} onChange={e=>setBulkForm(f=>({...f,employee_id:e.target.value}))}>
              <option value="">Select employee…</option>
              {users.map(u=><option key={u.id} value={u.id}>{u.name} — {u.job_title}</option>)}
            </Select>
          </Field>
          <Field label="Role type (KPI set)" required>
            <Select value={bulkForm.role_type} onChange={e=>setBulkForm(f=>({...f,role_type:e.target.value}))}>
              <option value="">Select role type…</option>
              {roleTypes.map(r=>{
                const count = (templatesByRole[r.key]||[]).length;
                return <option key={r.key} value={r.key}>{r.label} — {count} KPIs (weights sum to 100%)</option>;
              })}
            </Select>
          </Field>
          <Field label="Review cycle" required>
            <Select value={bulkForm.cycle_id} onChange={e=>setBulkForm(f=>({...f,cycle_id:e.target.value}))}>
              <option value="">Select cycle…</option>
              {cycles.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn type="button" variant="secondary" onClick={()=>setShowBulk(false)}>Cancel</Btn>
            <Btn type="submit" disabled={saving||!bulkForm.employee_id||!bulkForm.cycle_id||!bulkForm.role_type}>{saving?'Assigning…':'Assign KPI set'}</Btn>
          </div>
        </form>
      </Modal>

      {/* Self-eval modal */}
      <Modal open={!!selfModal} onClose={()=>setSelfModal(null)} title="Self-evaluation" width={540}>
        {selfModal && (
          <form onSubmit={handleSaveSelf}>
            <div style={{ fontSize:12, color:'var(--text-2)', marginBottom:14 }}>Rate yourself on each KPI (1=Poor · 2=Needs Improvement · 3=Meets Expectations · 4=Exceeds Expectations · 5=Outstanding). Your manager will review and add their scores.</div>
            {selfModal.kpis.map(k=>(
              <div key={k.id} style={{ background:'var(--gray-50)', borderRadius:8, padding:'10px', marginBottom:8 }}>
                <div style={{ fontSize:12, fontWeight:500, marginBottom:2 }}>{k.title}</div>
                <div style={{ fontSize:10, color:'var(--text-3)', marginBottom:6 }}>Weight: {k.weight}% · {k.target||'—'}</div>
                <Stars value={selfScores[k.id]?.self_score||0} onChange={v=>setSelfScores(s=>({...s,[k.id]:{...s[k.id],self_score:v}}))} />
                {(selfScores[k.id]?.self_score||0)>0 && <div style={{ fontSize:10, color:'#1F3B68', marginTop:3 }}>{KPI_SCORE_LABELS[selfScores[k.id]?.self_score]}</div>}
                <textarea value={selfScores[k.id]?.self_comment||''} placeholder="Evidence or context for your rating…"
                  onChange={e=>setSelfScores(s=>({...s,[k.id]:{...s[k.id],self_comment:e.target.value}}))}
                  style={{ width:'100%', fontSize:11, padding:'5px 8px', marginTop:6, border:'0.5px solid var(--border)', borderRadius:6, resize:'none', fontFamily:'inherit', minHeight:44, background:'var(--surface)', color:'var(--text-1)' }} />
              </div>
            ))}
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <Btn type="button" variant="secondary" onClick={()=>setSelfModal(null)}>Cancel</Btn>
              <Btn type="submit" disabled={saving}>{saving?'Saving…':'Submit self-evaluation'}</Btn>
            </div>
          </form>
        )}
      </Modal>

      {/* Manager scoring modal */}
      <Modal open={!!mgrModal} onClose={()=>setMgrModal(null)} title={`Manager scores — ${users.find(u=>String(u.id)===String(mgrModal?.employeeId))?.name}`} width={560}>
        {mgrModal && (
          <form onSubmit={handleSaveMgr}>
            {mgrModal.kpis.map(k=>(
              <div key={k.id} style={{ background:'var(--gray-50)', borderRadius:8, padding:'10px', marginBottom:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                  <div>
                    <div style={{ fontSize:12, fontWeight:500 }}>{k.title}</div>
                    <div style={{ fontSize:10, color:'var(--text-3)' }}>Weight: {k.weight}% · Target: {k.target||'—'}</div>
                  </div>
                </div>
                {k.self_score && (
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, padding:'5px 8px', background:'var(--surface)', borderRadius:5 }}>
                    <span style={{ fontSize:10, color:'var(--text-3)' }}>Self-eval:</span>
                    <div style={{ display:'flex', gap:2 }}>
                      {[1,2,3,4,5].map(n=>(<div key={n} style={{ width:16,height:16,borderRadius:3,background:n<=k.self_score?'#1F3B68':'var(--gray-100)',color:n<=k.self_score?'#fff':'var(--text-3)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9 }}>{n}</div>))}
                    </div>
                    <span style={{ fontSize:10, color:'#1F3B68', fontWeight:500 }}>{KPI_SCORE_LABELS[k.self_score]}</span>
                    {k.self_comment && <span style={{ fontSize:10, color:'var(--text-3)', fontStyle:'italic' }}>— "{k.self_comment}"</span>}
                  </div>
                )}
                <Stars value={mgrScores[k.id]?.score||0} onChange={v=>setMgrScores(s=>({...s,[k.id]:{...s[k.id],score:v}}))} />
                {(mgrScores[k.id]?.score||0)>0 && (
                  <div style={{ fontSize:10, color:'#1F3B68', marginTop:3 }}>
                    {KPI_SCORE_LABELS[mgrScores[k.id]?.score]} · +{((k.weight/100)*(mgrScores[k.id]?.score||0)).toFixed(2)} pts
                  </div>
                )}
                <textarea value={mgrScores[k.id]?.manager_comment||''} placeholder="Rationale or evidence for this score…"
                  onChange={e=>setMgrScores(s=>({...s,[k.id]:{...s[k.id],manager_comment:e.target.value}}))}
                  style={{ width:'100%', fontSize:11, padding:'5px 8px', marginTop:4, border:'0.5px solid var(--border)', borderRadius:5, resize:'none', fontFamily:'inherit', minHeight:42, background:'var(--surface)', color:'var(--text-1)' }} />
              </div>
            ))}
            {/* Live final */}
            {(() => {
              const total = mgrModal.kpis.reduce((s,k)=>s+((k.weight/100)*(mgrScores[k.id]?.score||0)),0);
              if (total===0) return null;
              const cal = scoreToCalibration(total);
              const r = CALIBRATION_MAP[cal];
              return (
                <div style={{ background:r?.bg, border:'0.5px solid', borderColor:r?.color+'44', borderRadius:8, padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                  <div style={{ fontSize:11, color:r?.color, fontWeight:500 }}>{r?.label} · {r?.bonusPct}% monthly salary bonus · Quarterly reward: PHP {roleReward(mgrModal.kpis[0]?.user_role_type).toLocaleString()}</div>
                  <div><span style={{ fontSize:22, fontWeight:500, color:r?.color }}>{total.toFixed(2)}</span><span style={{ fontSize:11, color:'var(--text-3)' }}>/5</span></div>
                </div>
              );
            })()}
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <Btn type="button" variant="secondary" onClick={()=>setMgrModal(null)}>Cancel</Btn>
              <Btn type="submit" disabled={saving}>{saving?'Saving…':'Save & finalise scores'}</Btn>
            </div>
          </form>
        )}
      </Modal>

      {/* Bonus modal */}
      <Modal open={!!bonusModal} onClose={()=>{setBonusModal(null);setBonusData(null);}} title={`Annual bonus — ${bonusModal?.name} · ${bonusYear}`} width={460}>
        {!bonusData ? <Spinner /> : (
          <div>
            {bonusData.cycle_scores?.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <SectionTitle>Quarterly scores ({bonusData.cycles_evaluated} cycles)</SectionTitle>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {bonusData.cycle_scores.map(cs=>(
                    <div key={cs.cycle_number} style={{ background:'var(--gray-100)', borderRadius:8, padding:'9px 12px', textAlign:'center', flex:1 }}>
                      <div style={{ fontSize:10, color:'var(--text-3)', marginBottom:3 }}>{cs.cycle}</div>
                      <div style={{ fontSize:20, fontWeight:500, color:scoreColor(cs.score) }}>{cs.score}</div>
                      <div style={{ fontSize:10, color:scoreColor(cs.score), marginTop:2 }}>{scoreToLabel(cs.score)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {bonusData.combined_score && (
              <>
                <div style={{ background:CALIBRATION_MAP[scoreToCalibration(bonusData.combined_score)]?.bg, borderRadius:10, padding:'14px 18px', textAlign:'center', marginBottom:14 }}>
                  <div style={{ fontSize:11, color:scoreColor(bonusData.combined_score), marginBottom:4 }}>Annual average (across {bonusData.cycles_evaluated} cycles)</div>
                  <div style={{ fontSize:38, fontWeight:500, color:scoreColor(bonusData.combined_score) }}>{bonusData.combined_score}</div>
                  <div style={{ fontSize:13, color:scoreColor(bonusData.combined_score), marginTop:2 }}>{scoreToLabel(bonusData.combined_score)}</div>
                </div>
                <div style={{ background:'#FFF7D6', borderRadius:8, padding:'12px 16px', textAlign:'center', marginBottom:14 }}>
                  <div style={{ fontSize:11, color:'#1F3B68', marginBottom:4 }}>Annual bonus</div>
                  <div style={{ fontSize:36, fontWeight:500, color:'#7A5E00' }}>{bonusData.bonus_pct_monthly}%</div>
                  <div style={{ fontSize:12, color:'#1F3B68' }}>of one monthly salary</div>
                </div>
              </>
            )}
            {!bonusData.saved && bonusData.combined_score && (
              <Btn onClick={handleGenerateBonus} disabled={saving} style={{ width:'100%', justifyContent:'center' }}>
                {saving?'Saving…':`Save ${bonusYear} bonus calculation`}
              </Btn>
            )}
            {bonusData.saved && <div style={{ textAlign:'center', color:'var(--navy-600)', fontSize:13 }}>✓ Saved</div>}
            {!bonusData.combined_score && (
              <div style={{ fontSize:13, color:'var(--text-3)', textAlign:'center' }}>
                No completed KPI cycles for {bonusYear} yet. Score all 3 cycles to generate annual bonus.
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Dev plan action item */}
      <Modal open={!!devModal} onClose={()=>setDevModal(null)} title={`Add development action — ${devModal?.name}`} width={400}>
        {devModal && (
          <form onSubmit={async e=>{e.preventDefault();setSaving(true);try{await addDevPlanItem({...devForm,user_id:devModal.employeeId});setDevModal(null);}catch{alert('Error');}finally{setSaving(false);}}}>
            <Field label="Action / goal" required><Input value={devForm.title} onChange={e=>setDevForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Improve test coverage to 80%" /></Field>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <Field label="Category"><Input value={devForm.category} onChange={e=>setDevForm(f=>({...f,category:e.target.value}))} placeholder="e.g. Technical" /></Field>
              <Field label="Due by"><Input type="date" value={devForm.due_date} onChange={e=>setDevForm(f=>({...f,due_date:e.target.value}))} /></Field>
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <Btn type="button" variant="secondary" onClick={()=>setDevModal(null)}>Cancel</Btn>
              <Btn type="submit" disabled={saving||!devForm.title}>{saving?'Saving…':'Add to dev plan'}</Btn>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
